import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { useSession } from '../../lib/auth-client';
import { api, type ApiSuccess, type Paginated } from '../../lib/api';

export function AdminIntegrationsPage() {
  const { data: session } = useSession();
  const role = (session?.user as { role?: string } | undefined)?.role;
  const [tab, setTab] = useState<'email' | 'sms' | 'payments' | 'seo'>('email');

  // 🔒 SUPER_ADMIN فقط
  if (role !== 'SUPER_ADMIN') {
    return (
      <div>
        <Card><CardBody>
          <div className="text-center py-12">
            <div className="text-6xl mb-3">🔒</div>
            <h2 className="text-xl font-black text-navy-dark mb-2">للسوبر أدمن فقط</h2>
            <p className="text-sm text-gray-500">
              صفحات الربط والتكاملات تحتوي معلومات حساسة (مفاتيح API، بيانات SMTP، ...) ومحجوبة لغير SUPER_ADMIN.
            </p>
          </div>
        </CardBody></Card>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-3xl font-black text-navy-dark">🔌 الربط والتكاملات</h1>
        <Badge color="red">🔒 SUPER_ADMIN</Badge>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        إعدادات حساسة لربط الخدمات الخارجية. كل التغييرات تُسجّل في سجل النشاط.
      </p>

      {/* 🚦 ملخص حالة الربط */}
      <ConnectionsOverview onSelectTab={setTab} />

      <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
        <Tab active={tab === 'email'}    onClick={() => setTab('email')}>
          📧 الإيميل (SMTP) <EmailStatusDot />
        </Tab>
        <Tab active={tab === 'sms'}      onClick={() => setTab('sms')}>
          📱 SMS <SmsStatusDot />
        </Tab>
        <Tab active={tab === 'payments'} onClick={() => setTab('payments')}>
          💳 بوابات الدفع <span className="inline-block w-2 h-2 rounded-full bg-gray-300 ml-1" />
        </Tab>
        <Tab active={tab === 'seo'} onClick={() => setTab('seo')}>
          🔍 SEO ومحركات البحث
        </Tab>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-sm text-blue-900 mb-6">
        💡 <b>سجل الإيميلات والـ SMS</b> ومتابعة الرسائل الفاشلة موجودة في
        <a href="/admin/messages-log" className="underline font-bold mx-1">📬 سجل الرسائل</a>
        — هذي الصفحة فقط للإعدادات والربط.
      </div>

      {tab === 'email'    && <EmailTab />}
      {tab === 'sms'      && <SmsTab />}
      {tab === 'payments' && <PaymentGatewaysTab />}
      {tab === 'seo'      && <SeoTab />}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Email (SMTP) ━━━━━━━━━━━━━━━━━━━━

function EmailTab() {
  const qc = useQueryClient();
  const [testTo, setTestTo] = useState('');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'email-settings'],
    queryFn: () => api.get<ApiSuccess<{
      smtp_host: string; smtp_port: string; smtp_secure: boolean;
      smtp_user: string; smtp_pass: string; smtp_pass_set: boolean;
      smtp_from_name: string; smtp_from_email: string;
    }>>('/api/admin/email/settings'),
  });

  const [f, setF] = useState({ smtp_host: '', smtp_port: '587', smtp_secure: false, smtp_user: '', smtp_pass: '', smtp_from_name: '', smtp_from_email: '' });

  // عبّئ الفورم لما يجي data
  useEffect(() => {
    if (data?.data) {
      const s = data.data;
      setF({
        smtp_host: s.smtp_host, smtp_port: s.smtp_port, smtp_secure: s.smtp_secure,
        smtp_user: s.smtp_user, smtp_pass: s.smtp_pass_set ? '••••••••' : '',
        smtp_from_name: s.smtp_from_name, smtp_from_email: s.smtp_from_email,
      });
    }
  }, [data]);

  const saveM = useMutation({
    mutationFn: () => api.put('/api/admin/email/settings', { ...f, smtp_port: Number(f.smtp_port) }),
    onSuccess: () => { setMsg({ type: 'success', text: '✓ تم حفظ الإعدادات' }); qc.invalidateQueries({ queryKey: ['admin', 'email-settings'] }); },
    onError: (e: Error) => setMsg({ type: 'error', text: '⛔ ' + e.message }),
  });

  const testConnM = useMutation({
    mutationFn: () => api.post('/api/admin/email/test', {}),
    onSuccess: () => setMsg({ type: 'success', text: '✓ الاتصال بـ SMTP ناجح' }),
    onError: (e: Error) => setMsg({ type: 'error', text: '⛔ ' + e.message }),
  });

  const sendTestM = useMutation({
    mutationFn: () => api.post('/api/admin/email/send-test', { to: testTo }),
    onSuccess: () => setMsg({ type: 'success', text: `✓ تم إرسال إيميل اختبار إلى ${testTo}` }),
    onError: (e: Error) => setMsg({ type: 'error', text: '⛔ ' + e.message }),
  });

  if (isLoading) return <div className="text-gray-500">⟳ جاري التحميل…</div>;

  return (
    <div className="space-y-4">
      {/* 💡 إرشادات */}
      <Card>
        <CardBody>
          <h3 className="font-black text-navy-dark mb-3">💡 إرشادات الإعداد</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="font-bold mb-1">🌐 Hostinger</div>
              <div className="text-xs text-gray-600">smtp.hostinger.com<br/>port 465 (SSL) / 587 (TLS)</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="font-bold mb-1">📧 Gmail</div>
              <div className="text-xs text-gray-600">smtp.gmail.com<br/>port 587 + App Password</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="font-bold mb-1">🌟 Office 365</div>
              <div className="text-xs text-gray-600">smtp.office365.com<br/>port 587</div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* الفورم */}
      <Card>
        <CardBody>
          <h3 className="font-black text-navy-dark mb-3">⚙️ إعدادات SMTP</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="SMTP Host *" value={f.smtp_host} onChange={(e) => setF({ ...f, smtp_host: e.target.value })} dir="ltr" placeholder="smtp.hostinger.com" />
            <Input label="Port *" type="number" value={f.smtp_port} onChange={(e) => setF({ ...f, smtp_port: e.target.value })} dir="ltr" />
            <Input label="اسم المستخدم/الإيميل *" value={f.smtp_user} onChange={(e) => setF({ ...f, smtp_user: e.target.value })} dir="ltr" />
            <Input label="كلمة المرور *" type="password" value={f.smtp_pass} onChange={(e) => setF({ ...f, smtp_pass: e.target.value })} dir="ltr" hint={f.smtp_pass === '••••••••' ? 'محفوظة — اتركها كذلك أو أدخل واحدة جديدة' : ''} />
            <Input label="اسم المرسل" value={f.smtp_from_name} onChange={(e) => setF({ ...f, smtp_from_name: e.target.value })} placeholder="شركة صورة العقارية" />
            <Input label="إيميل المرسل" value={f.smtp_from_email} onChange={(e) => setF({ ...f, smtp_from_email: e.target.value })} dir="ltr" placeholder="noreply@img.sa" />
            <label className="flex items-center gap-2 mt-2 md:col-span-2">
              <input type="checkbox" checked={f.smtp_secure} onChange={(e) => setF({ ...f, smtp_secure: e.target.checked })} className="w-5 h-5 accent-navy" />
              <span className="text-sm font-bold">🔒 SSL/TLS مباشر (port 465). اترك فارغاً لـ STARTTLS (port 587).</span>
            </label>
          </div>
          {msg && (
            <div className={`mt-4 rounded-lg p-3 text-sm ${msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
              {msg.text}
            </div>
          )}
          <div className="flex gap-2 mt-4 flex-wrap">
            <Button variant="primary" onClick={() => saveM.mutate()} loading={saveM.isPending}>💾 حفظ الإعدادات</Button>
            <Button variant="ghost" onClick={() => testConnM.mutate()} loading={testConnM.isPending}>🔌 اختبار الاتصال</Button>
          </div>
        </CardBody>
      </Card>

      {/* اختبار الإرسال */}
      <Card>
        <CardBody>
          <h3 className="font-black text-navy-dark mb-3">🧪 إرسال إيميل اختبار</h3>
          <div className="flex gap-2 flex-wrap">
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} dir="ltr" placeholder="test@example.com" />
            <Button variant="primary" onClick={() => sendTestM.mutate()} loading={sendTestM.isPending} disabled={!testTo}>📧 أرسل اختبار</Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ SMS ━━━━━━━━━━━━━━━━━━━━

function SmsTab() {
  const qc = useQueryClient();
  const [testTo, setTestTo] = useState('');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'sms-settings'],
    queryFn: () => api.get<ApiSuccess<{
      sms_provider: string; sms_sender_name: string;
      sms_unifonic_app_sid: string; sms_unifonic_app_sid_set: boolean;
      sms_mobily_username: string; sms_mobily_password: string; sms_mobily_password_set: boolean;
      sms_twilio_account_sid: string; sms_twilio_auth_token: string; sms_twilio_auth_token_set: boolean; sms_twilio_from: string;
      sms_msegat_username: string; sms_msegat_api_key: string; sms_msegat_api_key_set: boolean;
      sms_api_url: string; sms_api_key: string; sms_api_key_set: boolean; sms_api_method: string;
    }>>('/api/admin/sms/settings'),
  });

  const [f, setF] = useState({
    sms_provider: 'console', sms_sender_name: 'IMG',
    sms_unifonic_app_sid: '',
    sms_mobily_username: '', sms_mobily_password: '',
    sms_twilio_account_sid: '', sms_twilio_auth_token: '', sms_twilio_from: '',
    sms_msegat_username: '', sms_msegat_api_key: '',
    sms_api_url: '', sms_api_key: '', sms_api_method: 'POST',
  });

  useEffect(() => {
    if (data?.data) setF({ ...data.data });
  }, [data]);

  const saveM = useMutation({
    mutationFn: () => api.put('/api/admin/sms/settings', f),
    onSuccess: () => { setMsg({ type: 'success', text: '✓ تم حفظ الإعدادات' }); qc.invalidateQueries({ queryKey: ['admin', 'sms-settings'] }); qc.invalidateQueries({ queryKey: ['admin', 'sms-status'] }); },
    onError: (e: Error) => setMsg({ type: 'error', text: '⛔ ' + e.message }),
  });
  const testM = useMutation({
    mutationFn: () => api.post<ApiSuccess<{ provider: string }>>('/api/admin/sms/test', {}),
    onSuccess: (r) => setMsg({ type: 'success', text: `✓ ${r.data.provider} متصل` }),
    onError: (e: Error) => setMsg({ type: 'error', text: '⛔ ' + e.message }),
  });
  const sendTestM = useMutation({
    mutationFn: () => api.post('/api/admin/sms/send-test', { to: testTo }),
    onSuccess: () => setMsg({ type: 'success', text: `✓ تم إرسال SMS اختبار إلى ${testTo}` }),
    onError: (e: Error) => setMsg({ type: 'error', text: '⛔ ' + e.message }),
  });

  if (isLoading) return <div className="text-gray-500">⟳ جاري التحميل…</div>;

  return (
    <div className="space-y-4">
      {/* اختيار المزوّد */}
      <Card>
        <CardBody>
          <h3 className="font-black text-navy-dark mb-3">📱 المزوّد</h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {[
              { id: 'unifonic', label: '🇸🇦 Unifonic', sub: 'الأشهر سعودياً' },
              { id: 'msegat', label: '📨 Msegat', sub: 'موسيقات السعودي' },
              { id: 'mobily', label: '📡 Mobily', sub: 'موبايلي' },
              { id: 'twilio', label: '🌐 Twilio', sub: 'دولي' },
              { id: 'generic_http', label: '🔌 Generic HTTP', sub: 'مزود مخصّص' },
              { id: 'console', label: '🧪 Console', sub: 'للتطوير فقط' },
            ].map((p) => (
              <button
                key={p.id}
                onClick={() => setF({ ...f, sms_provider: p.id })}
                className={`p-3 rounded-xl border-2 text-right transition ${
                  f.sms_provider === p.id ? 'border-navy bg-navy/5' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-bold">{p.label}</div>
                <div className="text-[11px] text-gray-500">{p.sub}</div>
              </button>
            ))}
          </div>
          <div className="mt-3">
            <Input label="اسم المُرسل (Sender ID) — يجب اعتماده" value={f.sms_sender_name} onChange={(e) => setF({ ...f, sms_sender_name: e.target.value })} dir="ltr" placeholder="IMG" />
          </div>
        </CardBody>
      </Card>

      {/* إعدادات حسب المزوّد */}
      {f.sms_provider === 'unifonic' && (
        <Card>
          <CardBody>
            <h3 className="font-black text-navy-dark mb-3">🇸🇦 إعدادات Unifonic</h3>
            <Input label="App Sid" type="password" value={f.sms_unifonic_app_sid} onChange={(e) => setF({ ...f, sms_unifonic_app_sid: e.target.value })} dir="ltr" hint={data?.data.sms_unifonic_app_sid_set && f.sms_unifonic_app_sid === '••••••••' ? 'محفوظ — اتركه أو أدخل واحد جديد' : ''} />
            <p className="text-xs text-gray-500 mt-2">احصل على AppSid من <a href="https://el.cloud.unifonic.com" target="_blank" rel="noreferrer" className="text-navy hover:underline">Unifonic Dashboard</a></p>
          </CardBody>
        </Card>
      )}

      {f.sms_provider === 'mobily' && (
        <Card>
          <CardBody>
            <h3 className="font-black text-navy-dark mb-3">📡 إعدادات Mobily</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Username" value={f.sms_mobily_username} onChange={(e) => setF({ ...f, sms_mobily_username: e.target.value })} dir="ltr" />
              <Input label="API Key (Password)" type="password" value={f.sms_mobily_password} onChange={(e) => setF({ ...f, sms_mobily_password: e.target.value })} dir="ltr" />
            </div>
          </CardBody>
        </Card>
      )}

      {f.sms_provider === 'msegat' && (
        <Card>
          <CardBody>
            <h3 className="font-black text-navy-dark mb-3">📨 إعدادات Msegat</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Username (المستخدم)" value={f.sms_msegat_username} onChange={(e) => setF({ ...f, sms_msegat_username: e.target.value })} dir="ltr" placeholder="اسم المستخدم في msegat" />
              <Input label="API Key" type="password" value={f.sms_msegat_api_key} onChange={(e) => setF({ ...f, sms_msegat_api_key: e.target.value })} dir="ltr" hint={data?.data.sms_msegat_api_key_set && f.sms_msegat_api_key === '••••••••' ? 'محفوظ — اتركه أو أدخل واحد جديد' : ''} />
            </div>
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
              💡 احصل على بياناتك من <a href="https://www.msegat.com" target="_blank" rel="noreferrer" className="font-bold underline">msegat.com</a> ← لوحة التحكم ← API
              <br/>تأكد إن <b>اسم المُرسل (Sender ID)</b> أعلاه معتمَد من msegat (يطلب الاعتماد من Sender IDs في لوحتهم).
            </div>
          </CardBody>
        </Card>
      )}

      {f.sms_provider === 'twilio' && (
        <Card>
          <CardBody>
            <h3 className="font-black text-navy-dark mb-3">🌐 إعدادات Twilio</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="Account SID" value={f.sms_twilio_account_sid} onChange={(e) => setF({ ...f, sms_twilio_account_sid: e.target.value })} dir="ltr" />
              <Input label="Auth Token" type="password" value={f.sms_twilio_auth_token} onChange={(e) => setF({ ...f, sms_twilio_auth_token: e.target.value })} dir="ltr" />
              <Input label="From Number" value={f.sms_twilio_from} onChange={(e) => setF({ ...f, sms_twilio_from: e.target.value })} dir="ltr" placeholder="+1234567890" />
            </div>
          </CardBody>
        </Card>
      )}

      {f.sms_provider === 'generic_http' && (
        <Card>
          <CardBody>
            <h3 className="font-black text-navy-dark mb-3">🔌 إعدادات Generic HTTP</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input label="API URL" value={f.sms_api_url} onChange={(e) => setF({ ...f, sms_api_url: e.target.value })} dir="ltr" placeholder="https://api.example.com/sms" />
              <Input label="API Key" type="password" value={f.sms_api_key} onChange={(e) => setF({ ...f, sms_api_key: e.target.value })} dir="ltr" />
              <div>
                <label className="block text-sm font-bold text-ink mb-1">HTTP Method</label>
                <select value={f.sms_api_method} onChange={(e) => setF({ ...f, sms_api_method: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white">
                  <option value="POST">POST</option>
                  <option value="GET">GET</option>
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">يدعم placeholders: <code dir="ltr">{'{to}'}</code>, <code dir="ltr">{'{message}'}</code>, <code dir="ltr">{'{sender}'}</code></p>
          </CardBody>
        </Card>
      )}

      {f.sms_provider === 'console' && (
        <Card><CardBody>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900">
            🧪 <b>وضع Console:</b> الرسائل تُطبع في ترمنال السيرفر فقط (للتطوير). غيّر المزوّد لإرسال فعلي.
          </div>
        </CardBody></Card>
      )}

      {msg && (
        <div className={`rounded-lg p-3 text-sm ${msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {msg.text}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        <Button variant="primary" onClick={() => saveM.mutate()} loading={saveM.isPending}>💾 حفظ</Button>
        <Button variant="ghost" onClick={() => testM.mutate()} loading={testM.isPending} disabled={f.sms_provider === 'console'}>🔌 اختبار الاتصال</Button>
      </div>

      <Card>
        <CardBody>
          <h3 className="font-black text-navy-dark mb-3">🧪 إرسال SMS اختبار</h3>
          <div className="flex gap-2 flex-wrap">
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} dir="ltr" placeholder="+9665XXXXXXXX" />
            <Button variant="primary" onClick={() => sendTestM.mutate()} loading={sendTestM.isPending} disabled={!testTo}>📱 أرسل</Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Payment Gateways ━━━━━━━━━━━━━━━━━━━━

function PaymentGatewaysTab() {
  return (
    <Card>
      <CardBody>
        <div className="text-center py-12">
          <div className="text-5xl mb-3">💳</div>
          <h3 className="font-black text-navy-dark mb-2">إعدادات بوابات الدفع</h3>
          <p className="text-sm text-gray-500 mb-4">
            وسائل الدفع الأساسية تُدار من <a href="/admin/payments" className="text-navy hover:underline">صفحة المدفوعات</a>.
            <br/>
            مفاتيح API للبوابات (Moyasar / Tap / HyperPay) ستُضاف هنا قريباً.
          </p>
          <Badge color="amber">قيد التطوير</Badge>
        </div>
      </CardBody>
    </Card>
  );
}

// ملاحظة: سجل الإيميلات والـ SMS ودَاش الفشل في صفحة منفصلة
//   /admin/messages-log (AdminMessagesLogPage) — لتجنّب تكرار الإعدادات وتسهيل المراقبة

function Tab({ children, active, onClick }: { children: React.ReactNode; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-sm font-bold transition border-b-2 -mb-px whitespace-nowrap inline-flex items-center gap-1 ${
      active ? 'border-gold text-navy-dark' : 'border-transparent text-gray-500 hover:text-navy-dark'
    }`}>{children}</button>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ حالة الربط ━━━━━━━━━━━━━━━━━━━━

interface EmailStatus {
  configured: boolean;
  connected: boolean;
  connectionError?: string;
  host: string | null;
  sentToday: number;
  failedToday: number;
  lastSentAt: string | null;
  lastFailedAt: string | null;
  lastFailedError: string | null;
}

function useEmailStatus() {
  return useQuery({
    queryKey: ['admin', 'email-status'],
    queryFn: () => api.get<ApiSuccess<EmailStatus>>('/api/admin/email/status'),
    refetchInterval: 60_000,  // كل دقيقة
    retry: false,
  });
}

function EmailStatusDot() {
  const { data } = useEmailStatus();
  const s = data?.data;
  if (!s) return <span className="inline-block w-2 h-2 rounded-full bg-gray-300" />;
  if (!s.configured) return <span className="inline-block w-2 h-2 rounded-full bg-gray-400" title="لم يُكوّن" />;
  if (s.connected) return <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" title="متصل" />;
  return <span className="inline-block w-2 h-2 rounded-full bg-red-500" title="غير متصل" />;
}

function useSmsStatus() {
  return useQuery({
    queryKey: ['admin', 'sms-status'],
    queryFn: () => api.get<ApiSuccess<EmailStatus>>('/api/admin/sms/status'),
    refetchInterval: 60_000,
    retry: false,
  });
}

function SmsStatusDot() {
  const { data } = useSmsStatus();
  const s = data?.data;
  if (!s) return <span className="inline-block w-2 h-2 rounded-full bg-gray-300" />;
  if (!s.configured) return <span className="inline-block w-2 h-2 rounded-full bg-gray-400" title="لم يُكوّن" />;
  if (s.connected) return <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" title="متصل" />;
  return <span className="inline-block w-2 h-2 rounded-full bg-red-500" title="غير متصل" />;
}

function ConnectionsOverview({ onSelectTab }: { onSelectTab: (t: 'email' | 'sms' | 'payments') => void }) {
  const email = useEmailStatus();
  const sms = useSmsStatus();
  const s = email.data?.data;
  const sm = sms.data?.data;
  const isLoading = email.isLoading || sms.isLoading;
  const isFetching = email.isFetching || sms.isFetching;
  const refetch = () => { email.refetch(); sms.refetch(); };

  return (
    <Card className="mb-6">
      <CardBody>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-black text-navy-dark text-lg">🚦 حالة الربط الحالية</h3>
          <Button size="sm" variant="ghost" onClick={() => refetch()} loading={isFetching}>🔄 تحديث</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Email */}
          <button onClick={() => onSelectTab('email')} className="text-right">
            <div className={`rounded-xl p-4 border-2 transition hover:shadow ${
              !s ? 'bg-gray-50 border-gray-200' :
              !s.configured ? 'bg-gray-50 border-gray-300' :
              s.connected ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">📧</span>
                  <span className="font-black text-navy-dark">الإيميل (SMTP)</span>
                </div>
                {isLoading ? (
                  <span className="text-xs text-gray-500">⟳</span>
                ) : !s?.configured ? (
                  <Badge color="amber">غير مكوّن</Badge>
                ) : s.connected ? (
                  <Badge color="green">● متصل</Badge>
                ) : (
                  <Badge color="red">● غير متصل</Badge>
                )}
              </div>
              {s?.host && <div className="text-xs text-gray-500 font-mono mb-1" dir="ltr">{s.host}</div>}
              {s && (
                <div className="text-xs text-gray-600 mt-2 grid grid-cols-2 gap-2">
                  <div>📤 اليوم: <b>{s.sentToday}</b></div>
                  <div>❌ فشل: <b className={s.failedToday > 0 ? 'text-red-600' : ''}>{s.failedToday}</b></div>
                </div>
              )}
              {s?.connectionError && (
                <div className="text-xs text-red-600 mt-2 truncate" title={s.connectionError}>⚠️ {s.connectionError}</div>
              )}
            </div>
          </button>

          {/* SMS */}
          <button onClick={() => onSelectTab('sms')} className="text-right">
            <div className={`rounded-xl p-4 border-2 transition hover:shadow ${
              !sm ? 'bg-gray-50 border-gray-200' :
              !sm.configured ? 'bg-gray-50 border-gray-300' :
              sm.connected ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">📱</span>
                  <span className="font-black text-navy-dark">SMS</span>
                </div>
                {sms.isLoading ? (
                  <span className="text-xs text-gray-500">⟳</span>
                ) : !sm?.configured ? (
                  <Badge color="amber">غير مكوّن</Badge>
                ) : sm.connected ? (
                  <Badge color="green">● متصل</Badge>
                ) : (
                  <Badge color="red">● غير متصل</Badge>
                )}
              </div>
              {sm?.host && <div className="text-xs text-gray-500 font-mono mb-1" dir="ltr">{sm.host}</div>}
              {sm && (
                <div className="text-xs text-gray-600 mt-2 grid grid-cols-2 gap-2">
                  <div>📤 اليوم: <b>{sm.sentToday}</b></div>
                  <div>❌ فشل: <b className={sm.failedToday > 0 ? 'text-red-600' : ''}>{sm.failedToday}</b></div>
                </div>
              )}
              {sm?.connectionError && (
                <div className="text-xs text-red-600 mt-2 truncate" title={sm.connectionError}>⚠️ {sm.connectionError}</div>
              )}
            </div>
          </button>

          {/* Payment Gateways */}
          <button onClick={() => onSelectTab('payments')} className="text-right">
            <div className="rounded-xl p-4 border-2 border-gray-200 bg-gray-50 hover:shadow transition">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">💳</span>
                  <span className="font-black text-navy-dark">بوابات الدفع</span>
                </div>
                <Badge color="amber">قريباً</Badge>
              </div>
              <div className="text-xs text-gray-500">Moyasar / Tap / HyperPay</div>
            </div>
          </button>
        </div>
      </CardBody>
    </Card>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ 🔍 SEO Tab ━━━━━━━━━━━━━━━━━━━━

function SeoTab() {
  const [pingUrl, setPingUrl] = useState('');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const reindexAll = useMutation({
    mutationFn: () => api.post<ApiSuccess<{ count: number }>>('/api/admin/seo/reindex', {}),
    onSuccess: (r) => setMsg({ type: 'success', text: `✓ تم إخطار محركات البحث بـ ${r.data.count} رابط` }),
    onError: (e: Error) => setMsg({ type: 'error', text: '⛔ ' + e.message }),
  });

  const pingOne = useMutation({
    mutationFn: () => api.post('/api/admin/seo/ping', { url: pingUrl }),
    onSuccess: () => { setMsg({ type: 'success', text: `✓ تم إخطار محركات البحث بـ ${pingUrl}` }); setPingUrl(''); },
    onError: (e: Error) => setMsg({ type: 'error', text: '⛔ ' + e.message }),
  });

  return (
    <div className="space-y-4">
      <Card><CardBody>
        <h3 className="font-black text-navy-dark mb-2">🌐 حالة الفهرسة في محركات البحث</h3>
        <p className="text-sm text-gray-600 mb-4">
          المنصة تستخدم بروتوكول <b>IndexNow</b> لإخطار Bing/Yandex/Seznam/Naver فور نشر أي إعلان جديد،
          و <b>Google sitemap ping</b> لإخطار Google. هذا يقلّص زمن الفهرسة من أيام إلى دقائق.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <a href="/sitemap.xml" target="_blank" rel="noreferrer" className="block p-3 rounded-xl border-2 border-navy/20 hover:bg-navy-soft/30 transition text-center">
            <div className="text-2xl mb-1">🗺️</div>
            <div className="text-xs font-bold">sitemap.xml</div>
          </a>
          <a href="/robots.txt" target="_blank" rel="noreferrer" className="block p-3 rounded-xl border-2 border-navy/20 hover:bg-navy-soft/30 transition text-center">
            <div className="text-2xl mb-1">🤖</div>
            <div className="text-xs font-bold">robots.txt</div>
          </a>
          <a href="/sitemap-properties.xml" target="_blank" rel="noreferrer" className="block p-3 rounded-xl border-2 border-navy/20 hover:bg-navy-soft/30 transition text-center">
            <div className="text-2xl mb-1">🏘️</div>
            <div className="text-xs font-bold">العقارات</div>
          </a>
          <a href="/sitemap-auctions.xml" target="_blank" rel="noreferrer" className="block p-3 rounded-xl border-2 border-navy/20 hover:bg-navy-soft/30 transition text-center">
            <div className="text-2xl mb-1">🔨</div>
            <div className="text-xs font-bold">المزادات</div>
          </a>
        </div>
      </CardBody></Card>

      <Card><CardBody>
        <h3 className="font-black text-navy-dark mb-3">⚡ إعادة فهرسة شاملة</h3>
        <p className="text-sm text-gray-600 mb-4">
          أخطر محركات البحث الآن بكل الإعلانات النشطة على المنصة. مفيد بعد:
        </p>
        <ul className="text-sm text-gray-700 mb-4 list-disc pr-5 space-y-1">
          <li>إطلاق المنصة لأول مرة</li>
          <li>إضافة محتوى جديد بكميات كبيرة (Excel import مثلاً)</li>
          <li>تغيير هيكل الـ URLs أو تحديث جوهري</li>
        </ul>
        <Button variant="primary" onClick={() => reindexAll.mutate()} loading={reindexAll.isPending}>
          🚀 أخطر محركات البحث بكل المحتوى
        </Button>
      </CardBody></Card>

      <Card><CardBody>
        <h3 className="font-black text-navy-dark mb-3">🎯 إخطار رابط محدّد</h3>
        <p className="text-sm text-gray-600 mb-3">
          ادخل رابطاً واحداً لإعادة فهرسته فوراً (مثل صفحة عقار حدّثت بياناتها).
        </p>
        <div className="flex gap-2">
          <Input
            placeholder="https://img.sa/properties/abc123"
            value={pingUrl}
            onChange={(e) => setPingUrl(e.target.value)}
            dir="ltr"
            className="flex-1"
          />
          <Button variant="primary" onClick={() => pingOne.mutate()} loading={pingOne.isPending} disabled={!pingUrl.startsWith('https://img.sa/')}>
            📡 إخطار
          </Button>
        </div>
      </CardBody></Card>

      <Card><CardBody>
        <h3 className="font-black text-navy-dark mb-3">🛠️ روابط مفيدة</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="block p-3 rounded-lg border border-gray-200 hover:border-navy hover:bg-navy-soft/30 transition">
            🔵 <b>Google Search Console</b> — متابعة فهرسة Google، إرسال sitemap يدوياً، تشخيص الأخطاء
          </a>
          <a href="https://www.bing.com/webmasters" target="_blank" rel="noreferrer" className="block p-3 rounded-lg border border-gray-200 hover:border-navy hover:bg-navy-soft/30 transition">
            🟦 <b>Bing Webmaster Tools</b> — متابعة فهرسة Bing
          </a>
          <a href="https://search.google.com/test/rich-results" target="_blank" rel="noreferrer" className="block p-3 rounded-lg border border-gray-200 hover:border-navy hover:bg-navy-soft/30 transition">
            ⭐ <b>Rich Results Test</b> — اختبار JSON-LD وعرض النتائج المنسّقة
          </a>
          <a href="https://pagespeed.web.dev" target="_blank" rel="noreferrer" className="block p-3 rounded-lg border border-gray-200 hover:border-navy hover:bg-navy-soft/30 transition">
            🚀 <b>PageSpeed Insights</b> — قياس السرعة وتجربة المستخدم (مؤشر مهم لـ Google)
          </a>
        </div>
      </CardBody></Card>

      {msg && (
        <div className={`p-3 rounded-xl text-sm font-bold ${msg.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {msg.text}
        </div>
      )}
    </div>
  );
}
